/**
 * AOL Buying Guide Comparison Tool
 * ============================================================
 * HOW TO INSTALL
 * 1. Upload this file to your Shopify theme: Online Store > Themes >
 *    Edit code > Assets > Add a new asset > paste this file as
 *    comparison-tool.js
 *
 * 2. Load it in your theme layout. Open layout/theme.liquid and add
 *    the following line just before the closing </body> tag:
 *
 *    <script src="{{ 'comparison-tool.js' | asset_url }}" defer></script>
 *
 * 3. Add the CSS from aol-comparison-tool.css to your theme stylesheet,
 *    or upload it as a separate asset and link it in theme.liquid:
 *
 *    <link rel="stylesheet" href="{{ 'aol-comparison-tool.css' | asset_url }}">
 *
 * HOW IT WORKS PER BUYING GUIDE
 * 1. Add a custom.comparison_data metafield to the blog article.
 *    Type: JSON. Value: an array of Shopify product handles (see
 *    example-comparison-data.json for the full schema).
 *
 *
 * PRODUCT DATA
 * Prices, titles, and images are fetched live from Shopify's storefront
 * API using the /products/{handle}.js endpoint. No authentication is
 * required. Prices update automatically when changed in Shopify admin.
 *
 * SPEC DATA
 * Non-price specifications (material, dishwasher safe, knife friendly
 * etc.) are defined in the comparison_data metafield alongside the
 * product handles. These rarely change and are quick to update in the
 * metafield if needed.
 * ============================================================
 */

(function () {
  'use strict';
 
  // ============================================================
  // CONFIGURATION
  // ============================================================
 
  // SPEC_ROWS defines preferred display order and human-readable labels
  // for known spec keys. Any key present in the metafield data that is
  // NOT listed here will still appear as a row — it will just be sorted
  // after these known keys and its label will be auto-generated from the
  // key name (underscores replaced with spaces, title-cased).
  //
  // To add a new spec that you want to control the label or position of,
  // simply add an entry here. To use a spec without it appearing in every
  // guide, just include it in that guide's metafield data — it will only
  // show if at least one selected product has a non-empty value for it.
  const SPEC_ROWS = [
    // ---- Universal ----
    { key: 'material',            label: 'Material' },
    { key: 'construction',        label: 'Construction' },
    { key: 'dishwasher',          label: 'Dishwasher Safe' },
    { key: 'induction',           label: 'Induction Compatible' },
    { key: 'oven_safe',           label: 'Oven Safe' },
    { key: 'oven_temp',           label: 'Oven Safe To' },
    { key: 'hob_types',           label: 'Suitable Hob Types' },
    { key: 'warranty',            label: 'Warranty' },
    { key: 'made_in',             label: 'Made In' },
    // ---- Cookware ----
    { key: 'non_stick',           label: 'Non-Stick' },
    { key: 'coating',             label: 'Coating' },
    { key: 'seasoning',           label: 'Seasoning Required' },
    { key: 'heat_retention',      label: 'Heat Retention' },
    { key: 'weight',              label: 'Weight' },
    { key: 'thickness',           label: 'Thickness' },
    { key: 'handle_material',     label: 'Handle Material' },
    // ---- Chopping boards ----
    { key: 'knife_friendly',      label: 'Knife Friendly' },
    { key: 'antibacterial',       label: 'Antibacterial' },
    { key: 'maintenance',         label: 'Maintenance' },
    // ---- Knives ----
    { key: 'steel_type',          label: 'Steel Type' },
    { key: 'hardness',            label: 'Hardness (HRC)' },
    { key: 'edge_angle',          label: 'Edge Angle' },
    { key: 'forged_stamped',      label: 'Forged or Stamped' },
    { key: 'full_tang',           label: 'Full Tang' },
    // ---- Glassware / decanters ----
    { key: 'crystal',             label: 'Crystal' },
    { key: 'handmade',            label: 'Handmade' },
    { key: 'capacity',            label: 'Capacity' },
    { key: 'glass_shape',         label: 'Glass Shape' },
    { key: 'suitable_for',        label: 'Suitable For' },
    // ---- General ----
    { key: 'colours',             label: 'Available Colours' },
    { key: 'sizes',               label: 'Available Sizes' },
    { key: 'best_for',            label: 'Best For' },
  ];
 
  // Maximum number of products a user can compare simultaneously.
  const MAX_COMPARE = 3;
 
  // ============================================================
  // INITIALISATION
  // ============================================================
 
  document.addEventListener('DOMContentLoaded', function () {
    const containers = document.querySelectorAll('.aol-comparison-tool');
    containers.forEach(function (container) {
      initTool(container);
    });
  });
 
  // ============================================================
  // TOOL INITIALISATION
  // ============================================================
 
  function initTool(container) {
    let rawData;
 
    // Parse the comparison data from the data attribute.
    // The data attribute is populated by Liquid at render time.
    try {
      const encoded = container.getAttribute('data-comparison');
      if (!encoded) {
        console.warn('AOL Comparison Tool: no data-comparison attribute found.');
        return;
      }
      rawData = JSON.parse(decodeHTMLEntities(encoded));
    } catch (e) {
      console.error('AOL Comparison Tool: failed to parse comparison data.', e);
      return;
    }
 
    if (!rawData || !rawData.products || rawData.products.length === 0) {
      console.warn('AOL Comparison Tool: no products defined in comparison data.');
      return;
    }
 
    // Fetch live product data from Shopify storefront API for all products,
    // then build the UI once all fetches are complete.
    const fetchPromises = rawData.products.map(function (product) {
      return fetchProductData(product.handle).then(function (shopifyData) {
        return mergeProductData(product, shopifyData);
      });
    });
 
    Promise.all(fetchPromises)
      .then(function (products) {
        // Filter out any products that failed to fetch.
        const validProducts = products.filter(Boolean);
        if (validProducts.length === 0) {
          container.innerHTML = '<p class="aol-comparison-error">Product data could not be loaded. Please try again later.</p>';
          return;
        }
        buildUI(container, validProducts, rawData.label || 'Compare Products');
      })
      .catch(function (err) {
        console.error('AOL Comparison Tool: error loading products.', err);
        container.innerHTML = '<p class="aol-comparison-error">Product data could not be loaded. Please try again later.</p>';
      });
  }
 
  // ============================================================
  // SHOPIFY STOREFRONT API
  // ============================================================
 
  /**
   * Fetches live product data (title, price, image, URL) from Shopify's
   * unauthenticated /products/{handle}.js endpoint.
   * Returns null if the product is not found or the request fails.
   */
  function fetchProductData(handle) {
    return fetch('/products/' + handle + '.js')
      .then(function (response) {
        if (!response.ok) {
          console.warn('AOL Comparison Tool: product not found — ' + handle);
          return null;
        }
        return response.json();
      })
      .catch(function (err) {
        console.warn('AOL Comparison Tool: fetch failed for ' + handle, err);
        return null;
      });
  }
 
  /**
   * Merges live Shopify product data with the spec data from the metafield.
   * Shopify data provides: title, price, image, URL.
   * Metafield data provides: specs, group label, display name override.
   */
  function mergeProductData(metafieldProduct, shopifyData) {
    if (!shopifyData) return null;
 
    // Use the lowest available variant price.
    const price = shopifyData.variants && shopifyData.variants.length > 0
      ? Math.min.apply(null, shopifyData.variants.map(function (v) { return v.price; }))
      : null;
 
    // Format price as GBP. Shopify returns price in pence (integer).
    const formattedPrice = price !== null
      ? '£' + (price / 100).toFixed(2).replace(/\.00$/, '')
      : null;
 
    // Use compare_at_price to show a sale if present.
    const compareAt = shopifyData.variants && shopifyData.variants.length > 0
      ? Math.min.apply(null, shopifyData.variants
          .map(function (v) { return v.compare_at_price || 0; })
          .filter(function (p) { return p > 0; }))
      : null;
    const formattedCompareAt = compareAt
      ? '£' + (compareAt / 100).toFixed(2).replace(/\.00$/, '')
      : null;
 
    return {
      handle:         metafieldProduct.handle,
      title:          metafieldProduct.display_name || shopifyData.title,
      group:          metafieldProduct.group || '',
      price:          formattedPrice,
      compare_at:     formattedCompareAt,
      image:          shopifyData.featured_image || null,
      url:            '/products/' + metafieldProduct.handle,
      specs:          metafieldProduct.specs || {},
      available:      shopifyData.available,
    };
  }
 
  // ============================================================
  // UI BUILDER
  // ============================================================
 
  function buildUI(container, products, label) {
    container.innerHTML = '';
 
    // Group products for the dropdown if groups are defined.
    const grouped = groupProducts(products);
 
    // ---- Selector area ----
    const selectorWrap = document.createElement('div');
    selectorWrap.className = 'aol-comparison-selectors';
 
    for (let i = 0; i < MAX_COMPARE; i++) {
      const col = document.createElement('div');
      col.className = 'aol-comparison-selector-col';
 
      const lbl = document.createElement('label');
      lbl.textContent = i === 0 ? 'First product' : i === 1 ? 'Second product' : 'Third product (optional)';
      lbl.setAttribute('for', 'aol-compare-select-' + i);
 
      const select = document.createElement('select');
      select.id = 'aol-compare-select-' + i;
      select.className = 'aol-comparison-select';
      select.setAttribute('data-index', i);
 
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = 'Select a product\u2026';
      select.appendChild(placeholder);
 
      // Build optgroups if groups exist, otherwise flat options.
      if (Object.keys(grouped).length > 1 || (Object.keys(grouped).length === 1 && Object.keys(grouped)[0] !== '')) {
        Object.keys(grouped).forEach(function (groupName) {
          const optgroup = document.createElement('optgroup');
          optgroup.label = groupName || 'Other';
          grouped[groupName].forEach(function (product) {
            const opt = document.createElement('option');
            opt.value = product.handle;
            opt.textContent = product.title + (product.price ? ' \u2014 ' + product.price : '');
            optgroup.appendChild(opt);
          });
          select.appendChild(optgroup);
        });
      } else {
        products.forEach(function (product) {
          const opt = document.createElement('option');
          opt.value = product.handle;
          opt.textContent = product.title + (product.price ? ' \u2014 ' + product.price : '');
          select.appendChild(opt);
        });
      }
 
      col.appendChild(lbl);
      col.appendChild(select);
      selectorWrap.appendChild(col);
    }
 
    container.appendChild(selectorWrap);
 
    // ---- Compare button ----
    const btn = document.createElement('button');
    btn.className = 'aol-comparison-button';
    btn.textContent = 'Compare Selected Products';
    btn.type = 'button';
    container.appendChild(btn);
 
    // ---- Results area (hidden until compare is clicked) ----
    const resultsWrap = document.createElement('div');
    resultsWrap.className = 'aol-comparison-results';
    resultsWrap.setAttribute('aria-live', 'polite');
    container.appendChild(resultsWrap);
 
    // ---- Event listener ----
    btn.addEventListener('click', function () {
      const selects = container.querySelectorAll('.aol-comparison-select');
      const selectedHandles = [];
      selects.forEach(function (sel) {
        if (sel.value) selectedHandles.push(sel.value);
      });
 
      if (selectedHandles.length < 2) {
        resultsWrap.innerHTML = '<p class="aol-comparison-notice">Please select at least two products to compare.</p>';
        resultsWrap.classList.add('aol-comparison-results--visible');
        return;
      }
 
      // Deduplicate
      const unique = selectedHandles.filter(function (v, i, a) { return a.indexOf(v) === i; });
      if (unique.length < selectedHandles.length) {
        resultsWrap.innerHTML = '<p class="aol-comparison-notice">Please select different products to compare.</p>';
        resultsWrap.classList.add('aol-comparison-results--visible');
        return;
      }
 
      const selectedProducts = unique.map(function (handle) {
        return products.find(function (p) { return p.handle === handle; });
      }).filter(Boolean);
 
      renderResults(resultsWrap, selectedProducts);
      resultsWrap.classList.add('aol-comparison-results--visible');
 
      // Scroll results into view smoothly.
      setTimeout(function () {
        resultsWrap.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    });
  }
 
  // ============================================================
  // RESULTS TABLE RENDERER
  // ============================================================
 
  function renderResults(container, products) {
    container.innerHTML = '';
 
    const wrapper = document.createElement('div');
    wrapper.className = 'aol-comparison-table-wrapper';
 
    const table = document.createElement('table');
    table.className = 'aol-comparison-table';
 
    // ---- Header row: product titles only ----
    const thead = document.createElement('thead');
    const headerRow = document.createElement('tr');
 
    // Empty top-left cell
    const thEmpty = document.createElement('th');
    thEmpty.className = 'aol-comparison-th aol-comparison-th--label';
    thEmpty.scope = 'col';
    headerRow.appendChild(thEmpty);
 
    products.forEach(function (product) {
      const th = document.createElement('th');
      th.className = 'aol-comparison-th aol-comparison-th--product';
      th.scope = 'col';
 
      const titleEl = document.createElement('span');
      titleEl.className = 'aol-comparison-product-title';
      titleEl.textContent = product.title;
      th.appendChild(titleEl);
 
      headerRow.appendChild(th);
    });
 
    thead.appendChild(headerRow);
    table.appendChild(thead);
 
    // ---- Body rows ----
    const tbody = document.createElement('tbody');
 
    // ---- Image row: first row of tbody, one image per product column ----
    const imageRow = document.createElement('tr');
    imageRow.className = 'aol-comparison-row aol-comparison-row--image';
 
    const imageLabelCell = document.createElement('td');
    imageLabelCell.className = 'aol-comparison-cell aol-comparison-cell--label';
    imageRow.appendChild(imageLabelCell);
 
    products.forEach(function (product) {
      const td = document.createElement('td');
      td.className = 'aol-comparison-cell aol-comparison-cell--image';
      td.setAttribute('data-label', product.title);
 
      if (product.image) {
        const img = document.createElement('img');
        img.src = product.image;
        img.alt = product.title;
        img.className = 'aol-comparison-product-img';
        td.appendChild(img);
      }
 
      imageRow.appendChild(td);
    });
 
    tbody.appendChild(imageRow);
 
    // Price row
    const priceRow = buildRow('Price', products, function (product) {
      if (!product.price) return 'See product page';
      if (product.compare_at) {
        return product.price + ' <span class="aol-comparison-was">was ' + product.compare_at + '</span>';
      }
      return product.price;
    }, true);
    tbody.appendChild(priceRow);
 
    // Availability row
    const availRow = buildRow('Availability', products, function (product) {
      return product.available ? 'In stock' : 'Out of stock';
    });
    tbody.appendChild(availRow);
 
    // Spec rows — built dynamically from all spec keys present across the
    // selected products. Only rows where at least one product has a
    // non-empty value are shown. Order follows SPEC_ROWS where defined;
    // any unknown keys appear after in the order they were first seen.
    const specRowsToRender = resolveSpecRows(products);
    specRowsToRender.forEach(function (spec) {
      const row = buildRow(spec.label, products, function (product) {
        return product.specs[spec.key] || '\u2014';
      });
      tbody.appendChild(row);
    });
 
    // Shop button row
    const shopRow = document.createElement('tr');
    shopRow.className = 'aol-comparison-row aol-comparison-row--action';
 
    const shopLabelCell = document.createElement('td');
    shopLabelCell.className = 'aol-comparison-cell aol-comparison-cell--label';
    shopRow.appendChild(shopLabelCell);
 
    products.forEach(function (product) {
      const td = document.createElement('td');
      td.className = 'aol-comparison-cell';
 
      const link = document.createElement('a');
      link.href = product.url;
      link.className = 'aol-button-2026-green';
      link.textContent = 'View Product';
      td.appendChild(link);
 
      shopRow.appendChild(td);
    });
 
    tbody.appendChild(shopRow);
    table.appendChild(tbody);
    wrapper.appendChild(table);
    container.appendChild(wrapper);
  }
 
  /**
   * Builds a single table row.
   * @param {string} label - Row header text
   * @param {Array} products - Array of product objects
   * @param {Function} valueGetter - Function that returns cell value for a product
   * @param {boolean} allowHTML - Whether to render value as innerHTML (default false)
   */
  function buildRow(label, products, valueGetter, allowHTML) {
    const tr = document.createElement('tr');
    tr.className = 'aol-comparison-row';
 
    const th = document.createElement('th');
    th.className = 'aol-comparison-cell aol-comparison-cell--label';
    th.scope = 'row';
    th.textContent = label;
    tr.appendChild(th);
 
    products.forEach(function (product) {
      const td = document.createElement('td');
      td.className = 'aol-comparison-cell';
      td.setAttribute('data-label', label);
      const value = valueGetter(product);
      if (allowHTML) {
        td.innerHTML = value;
      } else {
        td.textContent = value;
      }
      tr.appendChild(td);
    });
 
    return tr;
  }
 
  // ============================================================
  // HELPERS
  // ============================================================
 
  /**
   * Resolves the spec rows to render for a given set of selected products.
   *
   * Logic:
   * 1. Collect every spec key that appears across all selected products
   *    and has at least one non-empty value.
   * 2. Order them: known keys from SPEC_ROWS first (in SPEC_ROWS order),
   *    then any remaining unknown keys in the order they were first seen.
   * 3. Return an array of { key, label } objects ready for rendering.
   *
   * This means:
   * - Rows with no data in any selected product are silently omitted.
   * - Any spec key in the metafield that is not in SPEC_ROWS still appears,
   *   with an auto-generated label derived from the key name.
   */
  function resolveSpecRows(products) {
    // Step 1: collect all populated keys across all products.
    const populatedKeys = {};
    products.forEach(function (product) {
      Object.keys(product.specs).forEach(function (key) {
        var val = product.specs[key];
        if (val && String(val).trim() !== '') {
          populatedKeys[key] = true;
        }
      });
    });
 
    // Step 2: build ordered list — known keys first, then unknowns.
    const result = [];
    const seen = {};
 
    // Known keys in SPEC_ROWS order, only if populated.
    SPEC_ROWS.forEach(function (spec) {
      if (populatedKeys[spec.key]) {
        result.push({ key: spec.key, label: spec.label });
        seen[spec.key] = true;
      }
    });
 
    // Unknown keys not in SPEC_ROWS, in first-seen order.
    products.forEach(function (product) {
      Object.keys(product.specs).forEach(function (key) {
        if (!seen[key] && populatedKeys[key]) {
          result.push({ key: key, label: keyToLabel(key) });
          seen[key] = true;
        }
      });
    });
 
    return result;
  }
 
  /**
   * Converts a snake_case or camelCase spec key into a readable label.
   * Examples: 'knife_friendly' -> 'Knife Friendly'
   *           'ovenSafeTemp'   -> 'Oven Safe Temp'
   */
  function keyToLabel(key) {
    return key
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }
 
  /**
   * Groups products by their 'group' property for optgroup rendering.
   */
  function groupProducts(products) {
    const groups = {};
    products.forEach(function (product) {
      const key = product.group || '';
      if (!groups[key]) groups[key] = [];
      groups[key].push(product);
    });
    return groups;
  }
 
  /**
   * Decodes HTML entities in a string.
   * Used to safely parse the JSON value from the data attribute,
   * which Shopify's Liquid `escape` filter will have HTML-encoded.
   */
  function decodeHTMLEntities(str) {
    const txt = document.createElement('textarea');
    txt.innerHTML = str;
    return txt.value;
  }
 
})();